import { DEFAULT_FLOOR_PLAN } from '../../utils/floorPlanGeometry.logic';
import React from 'react';
import { Info, AlertTriangle, Rows3, Trash2, Columns3, Check, Search, MapPin, CalendarClock, LockKeyhole } from 'lucide-react';
import { PPM, RackNode, RowConfig, RackOrientation, useEditorStore, formatRowRackLabel } from '../../store/editorStore';
import { FIELD_STYLE, ROW, LABEL, SECTION } from './PropertiesEditorStyles';
import { useConfirmStore } from '../../store/confirmStore';
import { isFacilityType, isArchitectureType } from '../../utils/floorEntities.logic';
import { snapToGrid, pxToTiles, tilesToPx } from '../../utils/cadSnapping.logic';
import { findNearestFreePosition, boxesIntersectOBB, Box2D } from '../../utils/collisionGuard.logic';
import { getRotatedTileSpan } from '../../utils/matrixWizard.logic';
import { FootprintChangeModal } from './FootprintChangeModal';
import {
  ALL_RACK_STATUSES,
  ALL_RACK_ROLES,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
  normalizeRackStatus,
  normalizeRackRole,
} from '../../utils/rackLifecycle.logic';
import { rackMembershipUpdate } from '../../utils/rackName.logic';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface Props {
  rack: RackNode;
  dcCode?: string;
  rows: Record<string, RowConfig>;
  dcSettings: any;
  racksCountByRow: Record<string, number>;
  onUpdate: (id: string, updates: Partial<RackNode>) => void;
  onDelete: (id: string) => void;
  onGenerateRow: (newRacks: RackNode[]) => void;
}

export const RackInspector: React.FC<Props> = ({ rack, dcCode, rows, dcSettings, racksCountByRow, onUpdate, onDelete }) => {
  const rackRow = rack.rowId ? rows[rack.rowId] : undefined;
  const allRacks = useEditorStore(s => s.racks);
  const planning = useEditorStore(s => s.dcSettings.planning) ?? { placements: [], rack_reservations: [] };
  const planned = planning.placements.find(plan => plan.rack_id === rack.dbId);
  const reserved = planning.rack_reservations.find(reservation => reservation.rack_id === rack.dbId);
  const navigate = useNavigate();
  const role = useAuthStore(state => state.userRole);
  const requestKey = React.useRef(crypto.randomUUID());
  const createMoveOrder = useMutation({
    mutationFn: () => api.post(`/api/datacenters/${dcCode}/impact-analysis/work-orders`, {
      datacenter_code: dcCode, title: `Перенос стойки ${rack.label}`, items: [{
        item_type: 'MOVE_RACK', description: `Перенести стойку ${rack.label} в опубликованную цель`,
        metadata: { rack_id: rack.dbId, planned_target: planned?.target },
      }],
    }, { headers: { 'X-Idempotency-Key': requestKey.current } }),
    onSuccess: result => navigate(`/${dcCode}/tasks?wo=${encodeURIComponent(result.data.id)}`),
    onError: (error: any) => setPlanningError(typeof error.response?.data?.detail === 'string'
      ? error.response.data.detail : 'Не удалось создать наряд на перенос'),
  });
  const [targetRow, setTargetRow] = React.useState(planned?.target.row_id ?? '');
  const [targetPosition, setTargetPosition] = React.useState(String(planned?.target.rack_pos_in_row ?? 1));
  const [targetX, setTargetX] = React.useState(String(planned?.target.x_m ?? rack.x / PPM));
  const [targetY, setTargetY] = React.useState(String(planned?.target.y_m ?? rack.y / PPM));
  const [planningError, setPlanningError] = React.useState('');
  const [reserveReason, setReserveReason] = React.useState(reserved?.reason ?? '');
  React.useEffect(() => {
    setTargetRow(planned?.target.row_id ?? '');
    setTargetPosition(String(planned?.target.rack_pos_in_row ?? 1));
    setTargetX(String(planned?.target.x_m ?? rack.x / PPM));
    setTargetY(String(planned?.target.y_m ?? rack.y / PPM));
    setReserveReason(reserved?.reason ?? '');
    setPlanningError('');
  }, [rack.id, planned?.target.row_id, planned?.target.rack_pos_in_row, planned?.target.x_m, planned?.target.y_m, reserved?.reason]);
  const floorPlan = dcSettings.floorPlan ?? DEFAULT_FLOOR_PLAN;
  const [rowSearch, setRowSearch] = React.useState('');
  const [positionError, setPositionError] = React.useState('');
  const [positionDraft, setPositionDraft] = React.useState(String(rack.rackPosInRow || 1));
  const tilePx = floorPlan.tileSizeMm / 1000 * PPM;
  const originX = floorPlan.originXMm / 1000 * PPM;
  const originY = floorPlan.originYMm / 1000 * PPM;

  React.useEffect(() => {
    setPositionDraft(String(rack.rackPosInRow || 1));
  }, [rack.id, rack.rackPosInRow]);

  const otherEntities: Box2D[] = React.useMemo(() => {
    return Object.values(allRacks)
      .filter(r => r.id !== rack.id)
      .map(r => ({
        id: r.id,
        x: r.x,
        y: r.y,
        w: r.widthM != null ? r.widthM * PPM : r.width,
        h: r.depthM != null ? r.depthM * PPM : r.height,
        rotation: r.rotation,
      }));
  }, [allRacks, rack.id]);

  const candidateBox: Box2D = React.useMemo(() => ({
    id: rack.id,
    x: rack.x,
    y: rack.y,
    w: rack.widthM != null ? rack.widthM * PPM : rack.width,
    h: rack.depthM != null ? rack.depthM * PPM : rack.height,
    rotation: rack.rotation,
  }), [rack.id, rack.x, rack.y, rack.widthM, rack.depthM, rack.width, rack.height, rack.rotation]);

  const collidingEntity = React.useMemo(() => {
    return otherEntities.find(e => boxesIntersectOBB(candidateBox, e));
  }, [candidateBox, otherEntities]);

  const collidingRack = collidingEntity ? allRacks[collidingEntity.id] : null;
  const hasCollision = Boolean(collidingEntity);

  const update = (field: keyof RackNode, value: any, extra?: Partial<RackNode>) => {
    const patch: Partial<RackNode> = { [field]: value, ...extra, isDirty: true };
    if (field === 'rotation') patch.orientation = value as RackOrientation;
    onUpdate(rack.id, patch);
  };

  const updateRowMembership = (rowId: string | undefined): boolean => {
    if (rowId) {
      // Assignment is a domain operation: it picks the next free position,
      // respects reserved row slots, and regenerates the canonical address.
      const assigned = useEditorStore.getState().assignRacksToRow([rack.id], rowId);
      if (!assigned) setPositionError('Стойка не назначена: выбранное место пересекается с другим объектом');
      return assigned;
    }
    const membership = rackMembershipUpdate(undefined, undefined, rack.rackPosInRow);
    update('rowId', membership.rowId, membership);
    return true;
  };

  const commitRackPosition = (value: string) => {
    if (!rack.rowId) return;
    if (!/^\d+$/.test(value.trim())) {
      setPositionError('Укажите целую позицию в ряду');
      return;
    }
    const position = parseInt(value, 10);
    if (position < 1) {
      setPositionError('Позиция должна быть не меньше 1');
      return;
    }
    const occupied = Object.values(allRacks).find(candidate => (
      candidate.id !== rack.id && candidate.rowId === rack.rowId && candidate.rackPosInRow === position
    ));
    const reserved = (rackRow?.positions ?? []).find(slot => (
      slot.position === position && slot.kind !== 'empty' && slot.kind !== 'rack'
    ));
    if (occupied || reserved) {
      setPositionError(occupied ? 'Позиция уже занята другой стойкой' : 'Позиция зарезервирована');
      return;
    }
    setPositionError('');
    useEditorStore.getState().assignRackToRowPosition(rack.id, rack.rowId, position);
  };

  const matchingRows = React.useMemo(() => {
    const query = rowSearch.trim().toLocaleLowerCase();
    return Object.values(rows)
      .filter(row => !query || [row.name, row.id, String(row.labelIndex), `ряд ${row.labelIndex}`]
        .some(value => String(value).toLocaleLowerCase().includes(query)))
      .sort((left, right) => left.labelIndex - right.labelIndex)
      .slice(0, 8);
  }, [rowSearch, rows]);

  const handleDeleteRack = async () => {
    const entityName = isPillar ? 'колонну' : isFacility ? rack.label || 'объект' : 'стойку';
    const confirmed = await useConfirmStore.getState().confirm({
      title: isPillar ? 'Удаление колонны' : isFacility ? 'Удаление объекта' : 'Удаление стойки',
      message: `Удалить ${entityName} "${rack.label}"?`,
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    });
    if (confirmed) {
      onDelete(rack.id);
    }
  };

  const isFacility = Boolean(rack.isFacility || isFacilityType(rack.rackType));
  const isPillar = Boolean(rack.isBlocked || isArchitectureType(rack.rackType));
  const unconfirmedPlace = !isPillar && !isFacility && (!rackRow || !Number.isInteger(rack.rackPosInRow) || rack.rackPosInRow! < 1);
  const currentStatus = normalizeRackStatus(rack.status);
  const currentRole = normalizeRackRole(rack.role, rack.rackType);
  const [cascadeToEquipment, setCascadeToEquipment] = React.useState(true);
  const [footprintDraft, setFootprintDraft] = React.useState<{ field: 'widthM' | 'depthM'; value: number } | null>(null);

  const requestFootprintChange = (field: 'widthM' | 'depthM', value: number) => {
    if (!Number.isFinite(value) || value <= 0) return;
    if (rackRow) setFootprintDraft({ field, value });
    else update(field, value);
  };

  return (
    <div style={{ padding: '0 12px 12px', fontSize: 12 }}>
      {!isFacility && !isPillar && rack.dbId && currentStatus === 'active' && <section aria-label="Планирование стойки" style={{ ...SECTION, marginBottom: 12 }}>
        <h3 style={{ fontSize: 12, margin: '0 0 8px' }}>Планирование</h3>
        {planned && <output aria-label="Целевое размещение" style={{ display: 'block', marginBottom: 8 }}>
          План: {planned.target.row_id && rows[planned.target.row_id]
            ? formatRowRackLabel(dcSettings, rows[planned.target.row_id], planned.target.rack_pos_in_row!)
            : 'Вне ряда'} · {planned.target.x_m.toFixed(2)}, {planned.target.y_m.toFixed(2)} м
        </output>}
        <label style={LABEL}>Целевой ряд<select style={FIELD_STYLE} value={targetRow} onChange={event => setTargetRow(event.target.value)}>
          <option value="">Вне ряда</option>{Object.values(rows).map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
        </select></label>
        {targetRow ? <label style={LABEL}>Целевое место<input style={FIELD_STYLE} type="number" min={1} step={1} value={targetPosition} onChange={event => setTargetPosition(event.target.value)} /></label>
          : <div style={{ display: 'flex', gap: 8 }}>
            <label style={LABEL}>Цель X, м<input style={FIELD_STYLE} type="number" step="0.001" value={targetX} onChange={event => setTargetX(event.target.value)} /></label>
            <label style={LABEL}>Цель Y, м<input style={FIELD_STYLE} type="number" step="0.001" value={targetY} onChange={event => setTargetY(event.target.value)} /></label>
          </div>}
        {planningError && <div role="alert">{planningError}</div>}
        <button type="button" style={FIELD_STYLE} onClick={() => setPlanningError(useEditorStore.getState().planRackMove(rack.id, targetRow || undefined, Number(targetPosition), Number(targetX), Number(targetY)) ?? '')}>
          <CalendarClock size={14} /> {planned ? 'Изменить план переноса' : 'Запланировать перенос'}
        </button>
        {planned && <button type="button" style={FIELD_STYLE} onClick={() => useEditorStore.getState().setPlanning({ ...planning, placements: planning.placements.filter(plan => plan.rack_id !== rack.dbId) })}><Trash2 size={14} /> Отменить план переноса</button>}
        {planned && dcCode && (role === 'dc_manager' || role === 'admin') && <button type="button" style={FIELD_STYLE}
          disabled={createMoveOrder.isPending} onClick={() => createMoveOrder.mutate()}>
          <CalendarClock size={14} /> Создать наряд на перенос
        </button>}
        <label style={LABEL}>Основание резерва всей стойки<input style={FIELD_STYLE} value={reserveReason} maxLength={255} onChange={event => setReserveReason(event.target.value)} /></label>
        <button type="button" style={FIELD_STYLE} disabled={!reserved && !reserveReason.trim()} onClick={() => useEditorStore.getState().setPlanning({ ...planning,
          rack_reservations: reserved ? planning.rack_reservations.filter(item => item.rack_id !== rack.dbId)
            : [...planning.rack_reservations, { id: crypto.randomUUID(), rack_id: rack.dbId!, reason: reserveReason.trim() }],
        })}><LockKeyhole size={14} /> {reserved ? 'Снять резерв всей стойки' : 'Зарезервировать всю стойку'}</button>
        {dcCode && <a href={`/${encodeURIComponent(dcCode)}/racks/${rack.dbId}`}>Диапазоны U в стойке</a>}
      </section>}
      <div style={{ fontWeight: 600, color: 'var(--color-accent-blue)', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{unconfirmedPlace ? 'Место не подтверждено' : rack.label}</span>
        {isPillar ? (
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(113, 113, 122, 0.2)', color: 'var(--text-secondary)', border: '1px solid rgba(113, 113, 122, 0.4)' }}>
            Колонна
          </span>
        ) : isFacility ? (
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--color-accent-purple-bg)', color: 'var(--color-accent-purple)', border: '1px solid var(--color-accent-purple-border)' }}>
            Инфраструктура
          </span>
        ) : null}
        <span style={{
          fontSize: 9,
          padding: '1px 5px',
          borderRadius: 3,
          background: RACK_STATUS_METAS[currentStatus].badgeBg,
          color: RACK_STATUS_METAS[currentStatus].color,
          border: `1px solid ${RACK_STATUS_METAS[currentStatus].badgeBorder}`,
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          {RACK_STATUS_METAS[currentStatus].labelEn}
        </span>
        <span style={{
          fontSize: 9,
          padding: '1px 5px',
          borderRadius: 3,
          background: RACK_ROLE_METAS[currentRole].badgeBg,
          color: RACK_ROLE_METAS[currentRole].color,
          border: `1px solid ${RACK_ROLE_METAS[currentRole].badgeBorder}`,
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: 700,
        }}>
          {RACK_ROLE_METAS[currentRole].shortCode}
        </span>
        {rack.isDirty && <span style={{ fontSize: 10, color: 'var(--status-warning)', marginLeft: 'auto' }}>● черновик</span>}
      </div>

      {!isPillar && !isFacility && (
        <>
          <div style={{
            marginBottom: 10,
            padding: '10px 11px',
            border: `1px solid ${rackRow ? 'var(--color-accent-blue-border)' : 'var(--status-warning-border)'}`,
            borderRadius: 6,
            background: rackRow ? 'var(--color-accent-blue-bg)' : 'var(--status-warning-bg)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7, color: rackRow ? 'var(--color-accent-blue)' : 'var(--status-warning-text)', fontWeight: 700 }}>
              <MapPin size={13} />
              {rackRow ? `Ряд ${rackRow.name}` : 'Стойка без адреса'}
            </div>
            {unconfirmedPlace && (
              <div role="note" style={{ color: 'var(--text-primary)', fontSize: 12, lineHeight: 1.5, overflowWrap: 'anywhere', marginBottom: 7 }}>
                <div>{rack.label && !['Без адреса', 'Адрес неизвестен'].includes(rack.label) ? `Сохранённая метка: ${rack.label}` : 'Сохранённая метка не задана'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Метка не является действующим адресом стойки.</div>
              </div>
            )}
            <div style={{ color: 'var(--text-muted)', fontSize: 10, lineHeight: 1.45 }}>
              {rackRow ? 'Выберите позицию или найдите другой ряд по имени.' : 'Найдите ряд по имени или номеру. После назначения стойка встанет в свободный слот ряда.'}
            </div>
            {rackRow && (
              <button
                type="button"
                onClick={() => { useEditorStore.getState().detachRackFromRow(rack.id); setPositionError(''); }}
                style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid var(--status-warning-border)', borderRadius: 4, padding: '5px 7px', background: 'transparent', color: 'var(--status-warning-text)', cursor: 'pointer', fontSize: 10 }}
              >
                <Trash2 size={12} />
                Убрать из ряда
              </button>
            )}
          </div>

          <div style={ROW}>
            <label style={LABEL} htmlFor="rack-row-search">Назначить в ряд</label>
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 8, top: 8, color: 'var(--text-muted)', pointerEvents: 'none' }} />
              <input
                id="rack-row-search"
                style={{ ...FIELD_STYLE, paddingLeft: 27 }}
                value={rowSearch}
                onChange={e => setRowSearch(e.target.value)}
                placeholder="Имя, номер или ID ряда"
                aria-label="Поиск ряда по имени"
              />
            </div>
            <div style={{ display: 'grid', gap: 3, marginTop: 5, maxHeight: 140, overflowY: 'auto' }}>
              {matchingRows.map(row => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => {
                    const assigned = updateRowMembership(row.id);
                    if (assigned) { setRowSearch(''); setPositionError(''); }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    border: `1px solid ${rack.rowId === row.id ? 'var(--color-accent-blue-border)' : 'var(--border-color)'}`,
                    borderRadius: 4, padding: '6px 8px', cursor: 'pointer', textAlign: 'left',
                    background: rack.rowId === row.id ? 'var(--color-accent-blue-bg)' : 'var(--bg-input)',
                    color: 'var(--text-primary)', fontSize: 11,
                  }}
                >
                  <span>{row.name}</span>
                  <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-data)' }}>#{row.labelIndex} · {racksCountByRow[row.id] || 0}</span>
                </button>
              ))}
              {!matchingRows.length && <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>Ряд не найден</span>}
            </div>
            {positionError && !rack.rowId && <div role="alert" style={{ color: 'var(--status-critical)', fontSize: 10, marginTop: 5 }}>{positionError}</div>}
          </div>

          {rack.rowId && (
            <div style={ROW}>
              <label style={LABEL} htmlFor="rack-pos">Позиция в ряду</label>
              <input id="rack-pos" style={FIELD_STYLE} type="number" min={1} value={positionDraft}
                onChange={e => { setPositionDraft(e.target.value); setPositionError(''); }}
                onBlur={e => commitRackPosition(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRackPosition(e.currentTarget.value); } }} />
              {positionError && <div style={{ color: 'var(--status-critical)', fontSize: 10, marginTop: 4 }}>{positionError}</div>}
            </div>
          )}
          {!rack.rowId && (
            <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', marginTop: -2, marginBottom: 4 }}>
              Выберите ряд из списка, чтобы добавить стойку в существующий ряд
            </div>
          )}
        </>
      )}

      {/* ── Status Lifecycle Chips ── */}
      <div style={ROW}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ ...LABEL, marginBottom: 0 }}>Статус жизненного цикла</label>
          <span style={{ fontSize: 9, color: RACK_STATUS_METAS[currentStatus].color, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
            {RACK_STATUS_METAS[currentStatus].labelEn.toUpperCase()}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 6 }}>
          {ALL_RACK_STATUSES.map(st => {
            const meta = RACK_STATUS_METAS[st];
            const isSel = currentStatus === st;
            return (
              <button
                key={st}
                type="button"
                onClick={() => update('status', st)}
                style={{
                  padding: '5px 4px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontFamily: 'JetBrains Mono, monospace',
                  fontWeight: isSel ? 600 : 400,
                  cursor: 'pointer',
                  border: `1px solid ${isSel ? meta.badgeBorder : 'var(--border-color, #374151)'}`,
                  background: isSel ? meta.badgeBg : 'var(--bg-card, #111827)',
                  color: isSel ? meta.color : 'var(--text-secondary, #9ca3af)',
                  transition: 'all 0.12s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}
                title={meta.description}
              >
                {isSel && <Check size={10} />}
                {meta.labelRu}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Functional Role Chips ── */}
      <div style={ROW}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ ...LABEL, marginBottom: 0 }}>Функциональная роль</label>
          <span style={{ fontSize: 9, color: RACK_ROLE_METAS[currentRole].color, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
            [{RACK_ROLE_METAS[currentRole].shortCode}]
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginBottom: 6 }}>
          {ALL_RACK_ROLES.map(rl => {
            const meta = RACK_ROLE_METAS[rl];
            const isSel = currentRole === rl;
            return (
              <button
                key={rl}
                type="button"
                onClick={() => update('role', rl)}
                style={{
                  padding: '4px 2px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontFamily: 'JetBrains Mono, monospace',
                  fontWeight: isSel ? 600 : 400,
                  cursor: 'pointer',
                  border: `1px solid ${isSel ? meta.badgeBorder : 'var(--border-color, #374151)'}`,
                  background: isSel ? meta.badgeBg : 'var(--bg-card, #111827)',
                  color: isSel ? meta.color : 'var(--text-secondary, #9ca3af)',
                  transition: 'all 0.12s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
                title={meta.description}
              >
                {isSel && <Check size={10} />}
                {meta.labelRu}
              </button>
            );
          })}
        </div>
      </div>

      <div style={ROW}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ ...LABEL, marginBottom: 0 }} htmlFor="rack-tenant">Арендатор (Tenant)</label>
          <button
            type="button"
            onClick={() => useEditorStore.getState().setShowRackAllocation(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-accent-blue, #60a5fa)',
              fontSize: 10,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: 0,
            }}
            title="Открыть матрицу распределения стоек по группам"
          >
            <Columns3 size={11} /> Матрица →
          </button>
        </div>

        {/* 1-click segmented quick chips */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginBottom: 6 }}>
          {[
            { key: 'mine', label: 'Своя', color: '#3b82f6', bg: 'rgba(59,130,246,0.18)', border: 'rgba(59,130,246,0.5)' },
            { key: 'other', label: 'Colocation', color: '#a855f7', bg: 'rgba(168,85,247,0.18)', border: 'rgba(168,85,247,0.5)' },
            { key: 'reserved', label: 'Резерв', color: '#f59e0b', bg: 'rgba(245,158,11,0.18)', border: 'rgba(245,158,11,0.5)' },
          ].map(opt => {
            const isSel = rack.tenant === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => update('tenant', opt.key)}
                style={{
                  padding: '5px 4px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: isSel ? 600 : 400,
                  cursor: 'pointer',
                  border: `1px solid ${isSel ? opt.border : 'var(--border-color, #374151)'}`,
                  background: isSel ? opt.bg : 'var(--bg-card, #111827)',
                  color: isSel ? opt.color : 'var(--text-secondary, #9ca3af)',
                  transition: 'all 0.12s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}
              >
                {isSel && <Check size={11} />}
                {opt.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {!rack.tenant && <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Арендатор не назначен</span>}
          <button type="button" disabled={!rack.tenant} onClick={() => update('tenant', null)}
            style={{ ...FIELD_STYLE, width: 'auto', cursor: rack.tenant ? 'pointer' : 'default', opacity: rack.tenant ? 1 : 0.5 }}>
            Снять арендатора
          </button>
        </div>

        {/* Cascade tenancy to equipment checkbox */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: 'var(--text-secondary, #9ca3af)',
            marginBottom: 6,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            data-testid="rack-inspector-cascade-checkbox"
            checked={cascadeToEquipment}
            onChange={e => {
              const checked = e.target.checked;
              setCascadeToEquipment(checked);
              update('cascadeTenancy', checked);
            }}
            style={{ accentColor: 'var(--color-accent-blue, #3b82f6)', cursor: 'pointer' }}
          />
          <span>Применить владельца ко всему установленному оборудованию</span>
        </label>

        {/* Custom tenant badge if custom */}
        {rack.tenant && !['mine', 'other', 'reserved'].includes(rack.tenant) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: 'var(--color-accent-blue, #60a5fa)', fontWeight: 500 }}>Кастомный тенант:</span>
            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}>
              {rack.tenant}
            </span>
          </div>
        )}
      </div>

      <div style={ROW}>
        <span style={LABEL}>{unconfirmedPlace ? 'Сохранённая метка' : isFacility || isPillar ? 'Название объекта' : 'Канонический адрес'}</span>
        <output id="rack-label" data-testid="rack-canonical-label" style={{ ...FIELD_STYLE, display: 'block', overflowWrap: 'anywhere' }}>
          {rack.label || 'Без адреса'}
        </output>
      </div>

      <div style={SECTION}>Размеры (физические)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-width">W (м)</label>
          <input id="rack-width" style={FIELD_STYLE} type="number" step={0.05} min={0.4} max={1.2}
            value={rack.widthM ?? ''} placeholder="-" aria-describedby={rack.widthM == null || rack.depthM == null ? 'rack-footprint-estimate' : undefined}
            onChange={e => e.target.value === '' ? update('widthM', null) : requestFootprintChange('widthM', e.target.valueAsNumber)} />
        </div>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-depth">D (м)</label>
          <input id="rack-depth" style={FIELD_STYLE} type="number" step={0.05} min={0.5} max={1.5}
            value={rack.depthM ?? ''} placeholder="-" aria-describedby={rack.widthM == null || rack.depthM == null ? 'rack-footprint-estimate' : undefined}
            onChange={e => e.target.value === '' ? update('depthM', null) : requestFootprintChange('depthM', e.target.valueAsNumber)} />
        </div>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-height">H (м)</label>
          <input id="rack-height" style={FIELD_STYLE} type="number" step="any" min={1.0} max={3.0}
            value={rack.heightM ?? ''} placeholder="-"
            onChange={e => update('heightM', e.target.value === '' ? null : e.target.valueAsNumber)} />
        </div>
      </div>
      {(rack.widthM == null || rack.depthM == null) && <p id="rack-footprint-estimate" role="status"
        style={{ margin: '0 0 12px', padding: '8px 0', borderTop: '1px solid var(--border-color)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        Ширина или глубина не указана. Контур оценочный: {(rack.width / 100).toFixed(2)} × {(rack.height / 100).toFixed(2)} м.
        {' '}Физическое размещение не подтверждено; сохранение других полей не заполняет размеры.
      </p>}
      {!isPillar && !isFacility && (
        <div style={{ marginBottom: 10 }}>
          <div style={ROW}>
            <label style={LABEL} htmlFor="rack-ucount">U-слоты</label>
            <input id="rack-ucount" style={FIELD_STYLE} type="number" min={0} step={1} aria-describedby="rack-u-height-warning"
              value={rack.uCount ?? ''} placeholder="Неизвестно"
              onChange={e => update('uCount', e.target.value === '' ? null : e.target.valueAsNumber)} />
          </div>
          <div id="rack-u-height-warning" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderTop: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.5 }}>
            <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1, color: 'var(--status-warning)' }} />
            <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              <div>Изменение количества U не меняет физическую высоту шкафа</div>
              <div>Физическая высота: {rack.heightM == null ? 'не указана' : `${rack.heightM} м`}</div>
            </div>
          </div>
        </div>
      )}
      {!isPillar && !isFacility && (
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-power">Лимит мощности (кВт)</label>
          <input id="rack-power" style={FIELD_STYLE} type="number" step="any" min={0} placeholder="Неизвестно"
            value={rack.max_power_w == null ? '' : rack.max_power_w / 1000}
            onChange={e => update('max_power_w', e.target.value === '' ? null : e.target.valueAsNumber * 1000)}
          />
        </div>
      )}
      <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', marginTop: -4, marginBottom: 10 }}>
        {isPillar
          ? 'Несущая колонна (архитектурное препятствие, 0U)'
          : isFacility
          ? 'Инфраструктурный агрегат (без U-слотов монтажа)'
          : 'Высота 1U = 44.45 мм. Лимит мощности используется в линзе «Мощность» и What-If.'}
      </div>

      {isFacility ? (
        <div style={{
          padding: '8px 10px',
          borderRadius: 5,
          background: 'var(--bg-hover)',
          border: '1px solid var(--border-color)',
          color: 'var(--text-secondary, #9ca3af)',
          fontSize: 11,
          marginTop: 14,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <Info size={13} style={{ flexShrink: 0, color: 'var(--color-accent-blue, #60a5fa)' }} />
          <span>{isPillar ? 'Колонны не содержат слотов монтажа оборудования' : 'Инженерные агрегаты не принимают монтаж IT-серверов'}</span>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, marginBottom: 12 }}>
          {rack.isDirty || !rack.dbId ? (
            <div
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: 'var(--bg-hover)', color: 'var(--text-muted, #6b7280)', padding: '8px 12px', borderRadius: 4,
                fontSize: 13, fontWeight: 600, cursor: 'not-allowed', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
              }}
              title={!rack.dbId ? 'Опубликуйте план, чтобы стойка появилась в базе данных' : 'Сохраните изменения на плане (опубликуйте черновик), чтобы перейти к управлению стойкой'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><line x1="9" y1="4" x2="9" y2="20"></line><line x1="15" y1="4" x2="15" y2="20"></line><line x1="4" y1="12" x2="20" y2="12"></line></svg>
              {!rack.dbId ? 'Управление стойкой (Требуется публикация)' : 'Управление стойкой (Требуется сохранение)'}
            </div>
          ) : (
            <a
              href={`/${dcCode}/racks/${rack.dbId}`}
              target="_blank"
              rel="noreferrer"
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: 'var(--status-normal, #10b981)', color: 'white', textDecoration: 'none', padding: '8px 12px',
                borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><line x1="9" y1="4" x2="9" y2="20"></line><line x1="15" y1="4" x2="15" y2="20"></line><line x1="4" y1="12" x2="20" y2="12"></line></svg>
              Управление стойкой
            </a>
          )}
        </div>
      )}

      <div style={SECTION}>Координаты (CAD и плитка 600×600 мм)</div>
      
      {/* Physical Meters */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-x">X (м)</label>
          <input
            id="rack-x"
            style={FIELD_STYLE}
            type="number"
            step={0.05}
            value={Number((rack.x / 100).toFixed(2))}
            onChange={e => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) update('x', Math.round(v * 100));
            }}
          />
        </div>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-y">Y (м)</label>
          <input
            id="rack-y"
            style={FIELD_STYLE}
            type="number"
            step={0.05}
            value={Number((rack.y / 100).toFixed(2))}
            onChange={e => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) update('y', Math.round(v * 100));
            }}
          />
        </div>
      </div>

      {/* Discrete Tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-tile-x">Плитка X (столбец)</label>
          <input
            id="rack-tile-x"
            style={{ ...FIELD_STYLE, color: '#60a5fa' }}
            type="number"
            step={1}
            min={0}
            value={pxToTiles(rack.x, tilePx, originX)}
            onChange={e => {
              const tile = parseInt(e.target.value, 10);
              if (!isNaN(tile) && tile >= 0) {
                update('x', tilesToPx(tile, tilePx, originX));
              }
            }}
          />
        </div>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-tile-y">Плитка Y (ряд)</label>
          <input
            id="rack-tile-y"
            style={{ ...FIELD_STYLE, color: '#60a5fa' }}
            type="number"
            step={1}
            min={0}
            value={pxToTiles(rack.y, tilePx, originY)}
            onChange={e => {
              const tile = parseInt(e.target.value, 10);
              if (!isNaN(tile) && tile >= 0) {
                update('y', tilesToPx(tile, tilePx, originY));
              }
            }}
          />
        </div>
      </div>

      {/* Tile metrics & snap */}
      {(() => {
        const tileSpan = getRotatedTileSpan(rack.widthM ?? rack.width / PPM, rack.depthM ?? rack.height / PPM, rack.rotation || 0);
        const isRotated = rack.rotation === 90 || rack.rotation === 270;
        return (
          <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{rack.widthM == null || rack.depthM == null ? 'Оценочно занимает:' : 'Занимает:'} {tileSpan.spanXStr} × {tileSpan.spanYStr} пл. {isRotated ? '(повернута)' : ''}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => {
                  onUpdate(rack.id, {
                    x: snapToGrid(rack.x, tilePx, originX),
                    y: snapToGrid(rack.y, tilePx, originY),
                    isDirty: true,
                  });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#60a5fa',
                  cursor: 'pointer',
                  fontSize: 10,
                  textDecoration: 'underline',
                }}
                title="Привязать к ближайшей плитке 600×600 мм"
              >
                К сетке
              </button>
              {hasCollision && (
                <button
                  onClick={() => {
                    const freePos = findNearestFreePosition(candidateBox, otherEntities, tilePx);
                    onUpdate(rack.id, {
                      x: freePos.x,
                      y: freePos.y,
                      isDirty: true,
                    });
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#34d399',
                    cursor: 'pointer',
                    fontSize: 10,
                    textDecoration: 'underline',
                  }}
                  title="Найти ближайшую свободную плитку"
                >
                  Свободная
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Collision alert badge if occupied */}
      {hasCollision && (
        <div style={{
          padding: '8px 10px',
          borderRadius: 5,
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid var(--status-critical, #ef4444)',
          color: 'var(--status-critical, #ef4444)',
          fontSize: 11,
          marginBottom: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
            <AlertTriangle size={13} style={{ flexShrink: 0 }} />
            <span>
              {collidingRack
                ? `Наложение на "${collidingRack.label || 'объект'}"`
                : 'Пересечение с объектом на сетке'}
            </span>
          </div>
          <div style={{ fontSize: 10, color: '#fca5a5' }}>
            Координаты: плитка [{pxToTiles(rack.x, tilePx, originX)}, {pxToTiles(rack.y, tilePx, originY)}]
          </div>
          <button
            onClick={() => {
              const freePos = findNearestFreePosition(candidateBox, otherEntities, tilePx);
              onUpdate(rack.id, {
                x: freePos.x,
                y: freePos.y,
                isDirty: true,
              });
            }}
            style={{
              alignSelf: 'flex-start',
              marginTop: 2,
              padding: '3px 8px',
              borderRadius: 4,
              background: 'var(--status-critical, #ef4444)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            Сместить на свободную плитку
          </button>
        </div>
      )}

      <div style={{ ...ROW, marginBottom: 14 }}>
        <label style={LABEL}>Поворот (Фронт)</label>
        <div style={{ display: 'flex', gap: 4 }}>
          {[0, 90, 180, 270].map(deg => (
            <button
              key={deg}
              onClick={() => update('rotation', deg)}
              style={{
                flex: 1, padding: '4px 0',
                background: rack.rotation === deg ? 'var(--color-accent-blue-bg, rgba(59,130,246,0.2))' : 'var(--bg-card, #111827)',
                border: rack.rotation === deg ? '1px solid var(--color-accent-blue-border, rgba(59,130,246,0.5))' : '1px solid var(--border-color, #374151)',
                borderRadius: 4, color: rack.rotation === deg ? 'var(--color-accent-blue, #60a5fa)' : 'var(--text-muted, #6b7280)', fontSize: 11, cursor: 'pointer',
              }}
            >
              {deg}°
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: 'var(--bg-card, #111827)', border: '1px solid var(--border-color-subtle, #1f2937)', padding: '10px', borderRadius: '6px', marginBottom: '14px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary, #9ca3af)', marginBottom: 6 }}>ГЕНЕРАТОР РЯДОВ</div>
        <button
          onClick={() => useEditorStore.getState().setShowRowGenerator(true)}
          style={{
            width: '100%',
            padding: '7px 10px',
            background: 'var(--color-accent-blue-bg, rgba(59,130,246,0.12))',
            border: '1px solid var(--color-accent-blue-border, rgba(59,130,246,0.3))',
            color: 'var(--color-accent-blue, #60a5fa)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Rows3 size={13} /> Конфигуратор рядов и коридоров (R)
        </button>
      </div>

      <button
        onClick={handleDeleteRack}
        style={{
          width: '100%',
          padding: '6px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
          borderRadius: 5,
          color: '#ef4444',
          fontSize: 12,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <Trash2 size={13} /> {isPillar ? 'Удалить колонну' : isFacility ? `Удалить ${rack.label || 'объект'}` : 'Удалить стойку'}
      </button>
      {footprintDraft && <FootprintChangeModal rackId={rack.id} field={footprintDraft.field} value={footprintDraft.value} onClose={() => setFootprintDraft(null)} />}
    </div>
  );
};
